import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useRef, useState } from "react";
import Papa from "papaparse";
import { Loader2, Upload as UploadIcon } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { SmsHubLayout } from "@/components/sms/SmsHubLayout";
import { uploadSmsContacts, listBatches, runMatchingChunk } from "@/server/sms.functions";
import { useAuth } from "@/hooks/useAuth";

export const Route = createFileRoute("/dashboard/admin/sms/upload")({
  head: () => ({ meta: [{ title: "Upload · SMS" }, { name: "robots", content: "noindex" }] }),
  component: Page,
});

type MatchProgress = {
  total: number;
  processed: number;
  matched: number;
  unmatched: number;
  staged: number;
  done: boolean;
};

function Page() {
  const { primaryRole } = useAuth();
  const qc = useQueryClient();
  const fileRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);

  const upload = useServerFn(uploadSmsContacts);
  const matchChunkFn = useServerFn(runMatchingChunk);
  const listFn = useServerFn(listBatches);

  const { data: batches } = useQuery({ queryKey: ["sms-batches"], queryFn: () => listFn(), enabled: primaryRole === "admin" });

  const [progress, setProgress] = useState<Record<string, MatchProgress>>({});
  const [runningBatch, setRunningBatch] = useState<string | null>(null);
  const cancelRef = useRef(false);

  const runMatching = async (batchId: string) => {
    if (runningBatch) {
      toast.error("Another match is already running");
      return;
    }
    setRunningBatch(batchId);
    cancelRef.current = false;
    setProgress((p) => ({
      ...p,
      [batchId]: { total: 0, processed: 0, matched: 0, unmatched: 0, staged: 0, done: false },
    }));
    let offset = 0;
    let totalStaged = 0;
    let reset = true;
    try {
      // eslint-disable-next-line no-constant-condition
      while (true) {
        if (cancelRef.current) break;
        const r: any = await matchChunkFn({ data: { batchId, offset, reset, chunk: 150 } });
        reset = false;
        totalStaged += r.staged ?? 0;
        setProgress((p) => ({
          ...p,
          [batchId]: {
            total: r.total ?? 0,
            processed: r.nextOffset ?? offset,
            matched: r.cumulativeMatched ?? 0,
            unmatched: r.cumulativeUnmatched ?? 0,
            staged: totalStaged,
            done: !!r.done,
          },
        }));
        if (r.done) {
          toast.success(
            `Matched ${r.cumulativeMatched}/${r.total} · Staged ${totalStaged} in queue`,
          );
          break;
        }
        offset = r.nextOffset;
      }
      qc.invalidateQueries({ queryKey: ["sms-batches"] });
      qc.invalidateQueries({ queryKey: ["sms-queue"] });
    } catch (e: any) {
      toast.error(e.message ?? "Match failed");
    } finally {
      setRunningBatch(null);
    }
  };

  const handleFile = (f: File) => {
    setUploading(true);
    Papa.parse(f, {
      header: true,
      skipEmptyLines: true,
      complete: async (res) => {
        try {
          const rows = (res.data as any[]).slice(0, 10000);
          const r: any = await upload({ data: { filename: f.name, rows } });
          toast.success(`Uploaded ${r.total} rows`);
          qc.invalidateQueries({ queryKey: ["sms-batches"] });
        } catch (e: any) {
          toast.error(e.message ?? "Upload failed");
        } finally {
          setUploading(false);
        }
      },
      error: (e) => {
        toast.error(e.message);
        setUploading(false);
      },
    });
  };


  return (
    <SmsHubLayout title="Upload SMS-Ready List">
      <div className="border-2 border-dashed border-border rounded-lg p-8 text-center bg-card">
        <UploadIcon className="h-8 w-8 mx-auto text-muted-foreground mb-3" />
        <p className="text-[13px] mb-1">Drop a CSV file with columns:</p>
        <p className="text-[12px] text-muted-foreground mb-4 font-mono">practice_name, provider_name, phone, npi, email, city, state, specialty</p>
        <input
          ref={fileRef}
          type="file"
          accept=".csv"
          className="hidden"
          onChange={(e) => e.target.files?.[0] && handleFile(e.target.files[0])}
        />
        <Button onClick={() => fileRef.current?.click()} disabled={uploading} size="sm">
          {uploading ? <><Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" />Uploading…</> : "Choose CSV"}
        </Button>
      </div>

      <h3 className="text-[14px] font-semibold mt-6 mb-2">Recent batches</h3>
      <div className="border border-border rounded-md">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>File</TableHead>
              <TableHead>Rows</TableHead>
              <TableHead>Matched</TableHead>
              <TableHead>Unmatched</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Uploaded</TableHead>
              <TableHead></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {(batches ?? []).length === 0 && (
              <TableRow><TableCell colSpan={7} className="text-center py-6 text-muted-foreground">No uploads yet.</TableCell></TableRow>
            )}
            {(batches ?? []).map((b: any) => {
              const p = progress[b.id];
              const isRunning = runningBatch === b.id;
              const pct = p && p.total > 0 ? Math.min(100, Math.round((p.processed / p.total) * 100)) : 0;
              return (
                <TableRow key={b.id}>
                  <TableCell className="font-medium">
                    {b.filename}
                    {(isRunning || p) && (
                      <div className="mt-1.5 space-y-1">
                        <div className="h-1.5 w-full bg-muted rounded overflow-hidden">
                          <div className="h-full bg-primary transition-all" style={{ width: `${pct}%` }} />
                        </div>
                        <div className="text-[11px] text-muted-foreground">
                          {p?.processed ?? 0}/{p?.total ?? b.row_count} · matched {p?.matched ?? 0} · staged {p?.staged ?? 0}
                          {isRunning && <span className="ml-1 inline-flex items-center"><Loader2 className="h-3 w-3 animate-spin" /></span>}
                        </div>
                      </div>
                    )}
                  </TableCell>
                  <TableCell>{b.row_count}</TableCell>
                  <TableCell className="text-emerald-700">{b.matched_count}</TableCell>
                  <TableCell className="text-muted-foreground">{b.unmatched_count}</TableCell>
                  <TableCell><span className="text-[11px] px-2 py-0.5 rounded-full bg-muted">{b.status}</span></TableCell>
                  <TableCell className="text-[12px] text-muted-foreground">{new Date(b.created_at).toLocaleString()}</TableCell>
                  <TableCell className="text-right space-x-2">
                    <Button size="sm" variant="outline" onClick={() => runMatching(b.id)} disabled={!!runningBatch}>
                      {isRunning ? <><Loader2 className="h-3.5 w-3.5 animate-spin mr-1" />Matching…</> : "Run matching"}
                    </Button>
                    <Link to="/dashboard/admin/sms/matching" search={{ batch: b.id } as any} className="text-[12px] text-primary font-medium">Review →</Link>
                  </TableCell>
                </TableRow>
              );
            })}

          </TableBody>
        </Table>
      </div>
    </SmsHubLayout>
  );
}
